import * as Sentry from "@sentry/react";
import mixpanel from "mixpanel-browser";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useIntercom } from "react-use-intercom";

import { useAuth } from "common/context/AuthContext";
import useCustomer from "common/datahooks/useCustomer";
import { initCustomerIO } from "common/helpers/customerIO";
import { identifyUser, initUserback } from "common/helpers/userbackClient";
import {
  getWhitelabelCustomization,
  isProductionEnvironment,
} from "common/helpers/utils";
import useCello from "common/hooks/useCello";
import useGTM from "common/hooks/useGTM";

async function syncTapfiliateRef(abortSignal: AbortSignal, ref: string | null) {
  if (ref) {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}tapfilliate/click`,
      {
        method: "POST",
        body: JSON.stringify({
          referral_code: ref,
        }),
        headers: {
          "Content-Type": "application/json",
        },
        signal: abortSignal,
      },
    );
    const json = await response.json();
    const expiration = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API has limited browser support
    document.cookie = `x-tapfilliate-token=${json.id}; expires=${expiration.toUTCString()}; domain=.aimfox.com; path=/;`;
    await window.syncer.heartbeat();
  }
}

export default function useAppInit() {
  const [searchParams] = useSearchParams();
  const { checkToken, isAuthorized } = useAuth();
  const { initializeCello, isReady: isCelloReady } = useCello(isAuthorized);
  const { boot } = useIntercom();
  const { customerInfo } = useCustomer();
  const { sendEvent } = useGTM();

  useEffect(() => {
    if (!!customerInfo && isAuthorized && isCelloReady) {
      if (!getWhitelabelCustomization()) {
        // Only initialize Cello when it's ready
        try {
          initializeCello(customerInfo);
        } catch (error) {
          // Log error but don't break the app
          console.error("Failed to initialize Cello:", error);
        }

        if (searchParams.get("signup")) {
          sendEvent("signed_up", ({ email }) => ({ email }));
        }
      }

      if (isProductionEnvironment()) {
        const {
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          chargebee_id: chargebeeId,
        } = customerInfo;

        const fullName = `${firstName} ${lastName}`;

        if (!getWhitelabelCustomization()) {
          initCustomerIO(chargebeeId, email);

          boot({
            email,
            name: fullName,
            phone,
            hideDefaultLauncher: true,
          });
          // Initialize and identify user in Userback
          initUserback().then(() => {
            identifyUser(chargebeeId, {
              name: fullName,
              email,
            });
          });
        }

        // Sentry
        Sentry.setTag("keycloak id", customerInfo.id);
        Sentry.setUser({
          id: chargebeeId,
          email,
          first_name: firstName,
          last_name: lastName,
        });

        // Mixpanel
        mixpanel.identify(chargebeeId);
        mixpanel.people.set({
          $email: email,
          $name: fullName,
        });
      }
    }
  }, [isAuthorized, customerInfo, isCelloReady]);

  useEffect(() => {
    const abortController = new AbortController();

    (async () => {
      const tapfiliateRef = searchParams.get("ref");
      if (tapfiliateRef && !getWhitelabelCustomization()) {
        await syncTapfiliateRef(abortController.signal, tapfiliateRef);
      }

      checkToken();
    })();

    return () => {
      abortController.abort("cleanup");
    };
  }, []);
}
